import { useEffect, useState } from "react";
import { differenceInCalendarDays, parseISO, format, isSameDay, addDays } from "date-fns";
import { PieChart, Pie, Cell, ResponsiveContainer } from "recharts";
import { VictoryChart, VictoryBar, VictoryAxis, VictoryTheme } from "victory";
import TrainingStats from "../staff/trainingstatsme";
import TaskTrainingIndv from "../manager/tasktrainingindv";
import MonthlyTasksChart from "./monthlytasks";

export default function StaffDetails({ staff }) {
  const [staffSummary, setStaffSummary] = useState([]);
  const [policies, setPolicies] = useState(null);
  const [lateReasons, setLateReasons] = useState([]);
  const [sickRecords, setSickRecords] = useState([]);
  const [sickTrends, setSickTrends] = useState('');
  const [sickTrendsGeneratedAt, setSickTrendsGeneratedAt] = useState(null);
  const [monthlyComparison, setMonthlyComparison] = useState({
    late: { current: 0, previous: 0, changePct: 0 },
    sick: { current: 0, previous: 0, changePct: 0 },
  });
  const [trendData, setTrendData] = useState([]);
  const [overtimeMonthlyHours, setOvertimeMonthlyHours] = useState([]);
  const [unacceptedBids, setUnacceptedBids] = useState([]);
  const [overtimeAcceptance, setOvertimeAcceptance] = useState({
    accepted: 0,
    unaccepted: 0,
    percentage: 0,
  });

  const parseCalendarDate = (value) => {
    if (!value) return null;
    if (value instanceof Date) return Number.isNaN(value.getTime()) ? null : value;
    if (typeof value === "string") {
      const directMatch = value.match(/(\d{4}-\d{2}-\d{2})/);
      if (directMatch) {
        const [year, month, day] = directMatch[1].split("-").map(Number);
        const localDate = new Date(year, month - 1, day);
        if (!Number.isNaN(localDate.getTime())) return localDate;
      }
    }
    const fallback = new Date(value);
    if (Number.isNaN(fallback.getTime())) return null;
    return new Date(fallback.getFullYear(), fallback.getMonth(), fallback.getDate());
  };

  const monthKey = (date) => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, "0");
    return `${year}-${month}`;
  };

  const calculatePctChange = (current, previous) => {
    if (previous === 0) return current === 0 ? 0 : 100;
    return Math.round(((current - previous) / previous) * 100);
  };

  const calculatePillFill = (current, previous) => {
    if (previous === 0) return current > 0 ? 100 : 0;
    return Math.max(0, Math.min(100, Math.round((current / previous) * 100)));
  };

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [policyResponse, backtoworkResponse, overtimeResponse] = await Promise.all([
          fetch("https://api.insightos.co.uk/api/staffpolicy", {
            method: "GET",
            headers: { "Content-Type": "application/json" },
            credentials: 'include',
          }),
          fetch("https://api.insightos.co.uk/api/backtoworkdata", {
            method: "GET",
            headers: { "Content-Type": "application/json" },
            credentials: 'include',
          }),
          fetch(`https://api.insightos.co.uk/api/staff/overtime-stats/${staff.id}`, {
            method: "GET",
            headers: { "Content-Type": "application/json" },
            credentials: 'include',
          })
        ]);

        if (!policyResponse.ok || !backtoworkResponse.ok) {
          console.error("Failed to fetch data");
          return;
        }

        const policyPayload = await policyResponse.json();
        const backToWorkPayload = await backtoworkResponse.json();
        const overtimePayload = overtimeResponse.ok ? await overtimeResponse.json() : null;

        const { success, clockins = [], shifts = [], holiday = [], data: policyEntries = [] } = policyPayload;

        if (!success) {
          console.error("Failed to fetch data");
          return;
        }

        const policiesData = policyEntries[0] || null;
        setPolicies(policiesData);

        const staffMember = staff;

        const personalClockins = clockins.filter(c => Number(c.StaffId) === Number(staffMember.id));
        const personalShifts = shifts.filter(s => Number(s.StaffId) === Number(staffMember.id));

        const now = new Date();
        const currentMonthDate = new Date(now.getFullYear(), now.getMonth(), 1);
        const previousMonthDate = new Date(now.getFullYear(), now.getMonth() - 1, 1);
        const currentMonthKey = monthKey(currentMonthDate);
        const previousMonthKey = monthKey(previousMonthDate);

        const policyWindowStart = parseCalendarDate(policyEntries[0]?.HolidayStart);
        const configuredPolicyEnd = parseCalendarDate(policyEntries[0]?.HolidayEnd);
        const policyWindowEnd = configuredPolicyEnd || (policyWindowStart ? addDays(policyWindowStart, 364) : null);
        const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
        const reportingEnd = policyWindowEnd && policyWindowEnd < today ? policyWindowEnd : today;
        const isInReportingWindow = (date) => Boolean(
          date && policyWindowStart && policyWindowEnd
          && date >= policyWindowStart
          && date <= reportingEnd
        );
        const effectivePolicyStart = policyWindowStart || previousMonthDate;
        const trendStart = new Date(effectivePolicyStart.getFullYear(), effectivePolicyStart.getMonth(), 1);
        const trendEnd = new Date(reportingEnd.getFullYear(), reportingEnd.getMonth(), 1);

        const monthlyBuckets = {};
        const monthSequence = [];
        let cursor = new Date(trendStart.getFullYear(), trendStart.getMonth(), 1);

        while (cursor <= trendEnd) {
          const key = monthKey(cursor);
          monthlyBuckets[key] = { late: 0, sick: 0, date: new Date(cursor) };
          monthSequence.push(key);
          cursor = new Date(cursor.getFullYear(), cursor.getMonth() + 1, 1);
        }

        personalClockins.forEach((clockin) => {
          const parsedDate = parseCalendarDate(clockin.Date);
          if (!isInReportingWindow(parsedDate)) return;
          const key = monthKey(parsedDate);
          if (!monthlyBuckets[key]) return;
          if (Number(clockin.Late) === 1) monthlyBuckets[key].late += 1;
        });

        personalShifts.forEach((shift) => {
          const parsedDate = parseCalendarDate(shift.ShiftDate);
          if (!isInReportingWindow(parsedDate)) return;
          const key = monthKey(parsedDate);
          if (!monthlyBuckets[key]) return;
          if (Number(shift.Sick) === 1) monthlyBuckets[key].sick += 1;
        });

        const trendSeries = monthSequence.map((key) => {
          const monthDate = monthlyBuckets[key].date;
          return {
            key,
            shortMonth: format(monthDate, "MMM yy"),
            fullMonth: format(monthDate, "MMMM yyyy"),
            late: monthlyBuckets[key].late,
            sick: monthlyBuckets[key].sick,
          };
        });

        setTrendData(trendSeries);

        const currentLate = monthlyBuckets[currentMonthKey]?.late || 0;
        const previousLate = monthlyBuckets[previousMonthKey]?.late || 0;
        const currentSick = monthlyBuckets[currentMonthKey]?.sick || 0;
        const previousSick = monthlyBuckets[previousMonthKey]?.sick || 0;

        setMonthlyComparison({
          late: {
            current: currentLate,
            previous: previousLate,
            changePct: calculatePctChange(currentLate, previousLate),
          },
          sick: {
            current: currentSick,
            previous: previousSick,
            changePct: calculatePctChange(currentSick, previousSick),
          },
        });

        // Calculate sick days and sick occasions (episodes)
        const sickDateValues = shifts
          .filter(s => Number(s.StaffId) === Number(staffMember.id) && Number(s.Sick) === 1)
          .map(s => parseCalendarDate(s.ShiftDate))
          .filter(isInReportingWindow);
        const sickDates = [...new Map(sickDateValues.map(date => [format(date, "yyyy-MM-dd"), date])).values()];

        let sickEpisodes = 0;
        if (sickDates.length > 0) {
          sickDates.sort((a, b) => a - b);
          sickEpisodes = 1;
          for (let i = 1; i < sickDates.length; i++) {
            const prevDate = sickDates[i - 1];
            const currentDate = sickDates[i];
            if (!isSameDay(currentDate, addDays(prevDate, 1))) {
              sickEpisodes++;
            }
          }
        }
        const sickDays = sickDates.length;

        const attendanceClockins = personalClockins.filter((clockin) => {
          const clockinDate = parseCalendarDate(clockin.Date);
          return isInReportingWindow(clockinDate) && Boolean(clockin.ClockIn);
        });
        const workedDays = new Set(
          attendanceClockins.map(clockin => format(parseCalendarDate(clockin.Date), "yyyy-MM-dd"))
        ).size;
        const workedHours = attendanceClockins.reduce(
          (total, clockin) => total + Number(clockin.HoursWorked),
          0
        );

        // Late reasons
        const lateClockIns = clockins.filter(
          c => Number(c.StaffId) === Number(staffMember.id)
            && Number(c.Late) === 1
            && isInReportingWindow(parseCalendarDate(c.Date))
        );
        const lateOccasions = lateClockIns.length;
        const lateReasonsData = lateClockIns.map((clockin) => {
          const minutesLate = clockin.MinutesLate || 0;
          const hours = Math.floor(minutesLate / 60);
          const minutes = minutesLate % 60;
          const formattedTime = hours > 0 ? `${hours}h ${minutes}m` : `${minutes}m`;
          return {
            ShiftDate: format(parseISO(clockin.Date), "dd/MM/yyyy"),
            ClockinTime: clockin.ClockIn,
            LateReason: clockin.LateReason || "No reason provided",
            MinutesLate: formattedTime,
          };
        });
        setLateReasons(lateReasonsData);

        // Holiday hours from staff record
        const holidayTaken = Number(staffMember.HolidayTaken) || 0;
        const holidayEntitlement = Number(staffMember.HolidayEntitlement) || 0;

        const lastAppraisal = staffMember.LastAppraisal
          ? format(parseISO(staffMember.LastAppraisal), "dd/MM/yyyy")
          : "No Data";
        const probationStatus = staffMember.Probation === 1 ? "Yes" : "No";

        const summary = {
          Name: staffMember.Name,
          SickDays: sickDays,
          SickEpisodes: sickEpisodes,
          LateOccasions: lateOccasions,
          WorkedDays: workedDays,
          WorkedHours: workedHours,
          HolidayDays: holidayTaken,
          HolidayEntitlement: holidayEntitlement,
          LastAppraisal: lastAppraisal,
          ProbationStatus: probationStatus,
        };

        setStaffSummary([summary]);

        if (backToWorkPayload?.success) {
          const personalRecords = Array.isArray(backToWorkPayload.data)
            ? backToWorkPayload.data.filter(record => Number(record.StaffId) === Number(staffMember.id))
            : [];
          setSickRecords(personalRecords);
          const trendRecord = Array.isArray(backToWorkPayload.staff)
            ? backToWorkPayload.staff.find(member => Number(member.StaffId) === Number(staffMember.id))
            : null;
          setSickTrends(trendRecord?.SickTrends ?? trendRecord?.StaffTrends ?? '');
          setSickTrendsGeneratedAt(trendRecord?.SickTrendsCreated ?? trendRecord?.StaffTrendsCreated ?? null);
        } else {
          setSickRecords([]);
          setSickTrends('');
          setSickTrendsGeneratedAt(null);
        }

        if (overtimePayload?.success) {
          setOvertimeMonthlyHours(Array.isArray(overtimePayload.monthlyAcceptedHours) ? overtimePayload.monthlyAcceptedHours : []);
          setUnacceptedBids(Array.isArray(overtimePayload.unacceptedBids) ? overtimePayload.unacceptedBids : []);
          setOvertimeAcceptance(overtimePayload.acceptance || { accepted: 0, unaccepted: 0, percentage: 0 });
        } else {
          setOvertimeMonthlyHours([]);
          setUnacceptedBids([]);
          setOvertimeAcceptance({ accepted: 0, unaccepted: 0, percentage: 0 });
        }

      } catch (error) {
        console.error("Error fetching data:", error);
      }
    };

    fetchData();
  }, [staff]);

  // ---- Pie chart data ----

  // Sick Episodes: use policies.SickEpisodes as the limit (default 3)
  const episodesUsed = staffSummary[0]?.SickEpisodes || 0;
  const episodesLimit = policies?.SickEpisodes || 3;
  const remainingEpisodes = Math.max(0, episodesLimit - episodesUsed);
  const sickEpisodesPieData = [
    { name: "Sick Episodes", value: episodesUsed },
    { name: "Policy Limit", value: remainingEpisodes },
  ];

  // Late Occasions: safe remaining
  const lateUsed = staffSummary[0]?.LateOccasions || 0;
  const lateLimit = policies?.LateOccasions || 0;
  const remainingLate = Math.max(0, lateLimit - lateUsed);
  const latePieData = [
    { name: "Late Occasions", value: lateUsed },
    { name: "Policy Limit", value: remainingLate },
  ];

  // Bradford Factor: episodes² × days
  const sickDays = staffSummary[0]?.SickDays || 0;
  const sickEpisodes = staffSummary[0]?.SickEpisodes || 0;
  const bradfordScore = Math.round((sickEpisodes * sickEpisodes) * sickDays);

  const getBradfordTrigger = (score) => {
    if (score < 50) return { label: 'Low', color: 'text-emerald-700', bg: 'bg-emerald-50 border-emerald-200' };
    if (score < 100) return { label: 'Monitor', color: 'text-blue-700', bg: 'bg-blue-50 border-blue-200' };
    if (score < 200) return { label: 'Review', color: 'text-amber-700', bg: 'bg-amber-50 border-amber-200' };
    if (score < 400) return { label: 'Formal Stage', color: 'text-orange-700', bg: 'bg-orange-50 border-orange-200' };
    return { label: 'Critical', color: 'text-rose-700', bg: 'bg-rose-50 border-rose-200' };
  };

  const bradfordTrigger = getBradfordTrigger(bradfordScore);

  // ---- Helper functions ----
  const formatDateSafe = (dateStr, pattern = "dd/MM/yyyy") => {
    if (!dateStr) return "";
    try {
      return format(parseISO(dateStr), pattern);
    } catch {
      return dateStr;
    }
  };

  const comparisonLabel = (changePct) => {
    if (changePct > 0) return `increased by ${changePct}% from last month`;
    if (changePct < 0) return `decreased by ${Math.abs(changePct)}% from last month`;
    return "no change from last month";
  };

  const policyStartDate = parseCalendarDate(policies?.HolidayStart);
  const configuredPolicyEndDate = parseCalendarDate(policies?.HolidayEnd);
  const policyEndDate = configuredPolicyEndDate || (policyStartDate ? addDays(policyStartDate, 364) : null);
  const policyWindowLabel = policyStartDate && policyEndDate
    ? `${format(policyStartDate, "dd/MM/yyyy")} - ${format(policyEndDate, "dd/MM/yyyy")}`
    : policyStartDate
      ? `${format(policyStartDate, "dd/MM/yyyy")} - ${format(addDays(policyStartDate, 365), "dd/MM/yyyy")}`
      : "Not set";

  const formatMonthKey = (monthKey) => {
    if (!monthKey) return "";
    try {
      return format(parseISO(`${monthKey}-01`), "MMM yy");
    } catch {
      return monthKey;
    }
  };

  const latePillFill = calculatePillFill(monthlyComparison.late.current, monthlyComparison.late.previous);
  const sickPillFill = calculatePillFill(monthlyComparison.sick.current, monthlyComparison.sick.previous);

  const lateBarData = trendData.map((item, index) => ({ x: index + 1, y: item.late }));
  const sickBarData = trendData.map((item, index) => ({ x: index + 1, y: item.sick }));
  const overtimeBarData = overtimeMonthlyHours.map((item, index) => ({ x: index + 1, y: item.hours }));

  // ---- Render ----
  return (
    <div className="min-h-screen bg-gradient-to-br from-indigo-50 via-white to-purple-50">
      {/* Header */}
      <div className="bg-gradient-to-r from-indigo-600 to-purple-600 px-4 py-6 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <div className="text-center sm:text-left">
            <h1 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-white mb-2">
              {staffSummary[0]?.Name || 'Staff Member'} Overview
            </h1>
            <p className="text-indigo-100 text-sm sm:text-base lg:text-lg">
              Comprehensive performance and attendance summary
            </p>
          </div>
        </div>
      </div>

      <div className="px-4 py-6 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">

          {/* Stats Grid */}
          <div className="grid grid-cols-1 sm:grid-cols-1 lg:grid-cols-2 xl:grid-cols-2 gap-4 sm:gap-6">

            {/* Sick Episodes (Pie Chart) */}
            <div className="bg-white/80 backdrop-blur-sm rounded-2xl shadow-xl border border-white/20 p-4 sm:p-6 hover:shadow-2xl transition-all duration-300">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg sm:text-xl font-semibold text-gray-900 flex items-center gap-2">
                  🤒 Sick Episodes
                </h3>
                <div className="text-right">
                  <p className="text-2xl sm:text-3xl font-bold text-red-600">
                    {episodesUsed}
                  </p>
                  <p className="text-xs text-gray-500">
                    of {episodesLimit} allowed
                  </p>
                  <p className="text-xs text-gray-500">
                    Review trigger: {episodesLimit}+ episodes
                  </p>
                </div>
              </div>
              <div className="mt-4 h-32 sm:h-40">
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie
                      data={sickEpisodesPieData}
                      dataKey="value"
                      outerRadius="80%"
                      fill="#8884d8"
                      label={false}
                    >
                      {sickEpisodesPieData.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={index === 0 ? "#ef4444" : "#10b981"} />
                      ))}
                    </Pie>
                  </PieChart>
                </ResponsiveContainer>
              </div>
              <div className="flex justify-center gap-4 text-xs">
                <div className="flex items-center gap-1">
                  <div className="w-3 h-3 bg-red-500 rounded-full"></div>
                  <span>Episodes</span>
                </div>
                <div className="flex items-center gap-1">
                  <div className="w-3 h-3 bg-green-500 rounded-full"></div>
                  <span>Available</span>
                </div>
              </div>
            </div>

            {/* Late Occasions (Pie Chart) */}
            <div className="bg-white/80 backdrop-blur-sm rounded-2xl shadow-xl border border-white/20 p-4 sm:p-6 hover:shadow-2xl transition-all duration-300">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg sm:text-xl font-semibold text-gray-900 flex items-center gap-2">
                  ⏰ Late Occasions
                </h3>
                <div className="text-right">
                  <p className="text-2xl sm:text-3xl font-bold text-orange-600">
                    {lateUsed}
                  </p>
                  <p className="text-xs text-gray-500">
                    of {lateLimit} allowed
                  </p>
                </div>
              </div>
              <div className="mt-4 h-32 sm:h-40">
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie
                      data={latePieData}
                      dataKey="value"
                      outerRadius="80%"
                      fill="#8884d8"
                      label={false}
                    >
                      {latePieData.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={index === 0 ? "#f59e0b" : "#10b981"} />
                      ))}
                    </Pie>
                  </PieChart>
                </ResponsiveContainer>
              </div>
              <div className="flex justify-center gap-4 text-xs">
                <div className="flex items-center gap-1">
                  <div className="w-3 h-3 bg-orange-500 rounded-full"></div>
                  <span>Late</span>
                </div>
                <div className="flex items-center gap-1">
                  <div className="w-3 h-3 bg-green-500 rounded-full"></div>
                  <span>Available</span>
                </div>
              </div>
            </div>

            {/* Holiday Hours */}
            <div className="bg-white/80 backdrop-blur-sm rounded-2xl shadow-xl border border-white/20 p-4 sm:p-6 hover:shadow-2xl transition-all duration-300">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg sm:text-xl font-semibold text-gray-900 flex items-center gap-2">
                  🏖️ Holiday Hours
                </h3>
                <div className="text-right">
                  <p className="text-2xl sm:text-3xl font-bold text-blue-600">
                    {staffSummary[0]?.HolidayDays || 0}
                  </p>
                  <p className="text-xs text-gray-500">
                    of {staffSummary[0]?.HolidayEntitlement || 0} total hours
                  </p>
                </div>
              </div>
              <div className="mt-4">
                <div className="w-full bg-gray-200 rounded-full h-4">
                  <div 
                    className="bg-gradient-to-r from-blue-500 to-cyan-500 h-4 rounded-full transition-all duration-500"
                    style={{ 
                      width: `${Math.min(((staffSummary[0]?.HolidayDays || 0) / (staffSummary[0]?.HolidayEntitlement || 1)) * 100, 100)}%` 
                    }}
                  ></div>
                </div>
                <p className="text-center text-sm text-gray-600 mt-2">
                  {Math.round(((staffSummary[0]?.HolidayDays || 0) / (staffSummary[0]?.HolidayEntitlement || 1)) * 100)}% used
                </p>
              </div>
            </div>

            {/* Sick Days (numeric) */}
            <div className="bg-white/80 backdrop-blur-sm rounded-2xl shadow-xl border border-white/20 p-4 sm:p-6 hover:shadow-2xl transition-all duration-300">
              <div className="text-center">
                <h3 className="text-lg sm:text-xl font-semibold text-gray-900 mb-2 flex items-center justify-center gap-2">
                  🏥 Sick Days
                </h3>
                <p className="text-3xl sm:text-4xl font-bold text-purple-600 mb-2">
                  {sickDays}
                </p>
                <p className="text-sm text-gray-600">
                  Total days lost to sickness
                </p>
              </div>
            </div>

            {/* Bradford Score */}
            <div className="bg-white/80 backdrop-blur-sm rounded-2xl shadow-xl border border-white/20 p-4 sm:p-6 hover:shadow-2xl transition-all duration-300">
              <div className="text-center">
                <h3 className="text-lg sm:text-xl font-semibold text-gray-900 mb-2 flex items-center justify-center gap-2">
                  🎯 Bradford Score
                </h3>
                <p className="text-3xl sm:text-4xl font-bold text-slate-700 mb-2">{bradfordScore}</p>
                <div className={`mx-auto mt-3 inline-flex items-center rounded-full border px-3 py-1 text-xs font-semibold ${bradfordTrigger.bg} ${bradfordTrigger.color}`}>
                  Indicator: {bradfordTrigger.label}
                </div>
                <p className="text-xs text-gray-500 mt-2">Formula: episodes² × days</p>
                <p className="text-xs text-gray-500 mt-1">Period: {policyWindowLabel}</p>
                <p className="text-xs text-gray-500 mt-1">Use as a review prompt, not an automatic decision.</p>
              </div>
            </div>

            {/* Time Worked */}
            <div className="bg-white/80 backdrop-blur-sm rounded-2xl shadow-xl border border-white/20 p-4 sm:p-6 hover:shadow-2xl transition-all duration-300">
              <div className="text-center">
                <h3 className="text-lg sm:text-xl font-semibold text-gray-900 mb-3">Time Worked</h3>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <p className="text-3xl sm:text-4xl font-bold text-cyan-700">{staffSummary[0]?.WorkedDays || 0}</p>
                    <p className="text-sm text-gray-600">days</p>
                  </div>
                  <div>
                    <p className="text-3xl sm:text-4xl font-bold text-emerald-700">
                      {Number(staffSummary[0]?.WorkedHours || 0).toFixed(1)}
                    </p>
                    <p className="text-sm text-gray-600">hours</p>
                  </div>
                </div>
                <p className="text-xs text-gray-500 mt-3">Recorded attendance since policy start; hours update after clock-out</p>
              </div>
            </div>

            {/* Probation Status */}
            <div className="bg-white/80 backdrop-blur-sm rounded-2xl shadow-xl border border-white/20 p-4 sm:p-6 hover:shadow-2xl transition-all duration-300">
              <div className="text-center">
                <h3 className="text-lg sm:text-xl font-semibold text-gray-900 mb-2 flex items-center justify-center gap-2">
                  📋 Probation Status
                </h3>
                <p className={`text-3xl sm:text-4xl font-bold mb-2 ${
                  staffSummary[0]?.ProbationStatus === 'Yes' ? 'text-yellow-600' : 'text-green-600'
                }`}>
                  {staffSummary[0]?.ProbationStatus}
                </p>
                <p className="text-sm text-gray-600">
                  Current status
                </p>
              </div>
            </div>

            {/* Last Appraisal */}
            <div className="bg-white/80 backdrop-blur-sm rounded-2xl shadow-xl border border-white/20 p-4 sm:p-6 hover:shadow-2xl transition-all duration-300">
              <div className="text-center">
                <h3 className="text-lg sm:text-xl font-semibold text-gray-900 mb-2 flex items-center justify-center gap-2">
                  📊 Last Appraisal
                </h3>
                <p className="text-lg sm:text-xl font-bold text-indigo-600 mb-2">
                  {staffSummary[0]?.LastAppraisal}
                </p>
                <p className="text-sm text-gray-600">
                  Most recent review
                </p>
              </div>
            </div>
          </div>

          {/* Monthly Comparison */}
          <div className="mt-8">
            <div className="bg-white/80 backdrop-blur-sm rounded-2xl shadow-xl border border-white/20 overflow-hidden">
              <div className="px-6 py-4 bg-gradient-to-r from-blue-100 to-indigo-100 border-b border-blue-200">
                <h3 className="text-xl font-semibold text-gray-900">Monthly Comparison</h3>
                <p className="text-sm text-gray-600 mt-1">
                  This month vs last month for lateness and sickness
                </p>
              </div>
              <div className="p-6 grid grid-cols-1 lg:grid-cols-2 gap-6">
                <div className="rounded-xl border border-orange-100 bg-orange-50/70 p-4">
                  <p className="text-sm font-semibold text-orange-700">Lateness</p>
                  <p className="mt-1 text-sm text-gray-700">
                    {comparisonLabel(monthlyComparison.late.changePct)}
                  </p>
                  <p className="mt-1 text-xs text-gray-600">
                    {monthlyComparison.late.current} this month • {monthlyComparison.late.previous} last month
                  </p>
                  <div className="mt-3 h-4 w-full rounded-full bg-white border border-orange-200 overflow-hidden">
                    <div
                      className="h-full rounded-full bg-orange-500 transition-all duration-500"
                      style={{ width: `${latePillFill}%` }}
                    ></div>
                  </div>
                </div>

                <div className="rounded-xl border border-red-100 bg-red-50/70 p-4">
                  <p className="text-sm font-semibold text-red-700">Sickness</p>
                  <p className="mt-1 text-sm text-gray-700">
                    {comparisonLabel(monthlyComparison.sick.changePct)}
                  </p>
                  <p className="mt-1 text-xs text-gray-600">
                    {monthlyComparison.sick.current} this month • {monthlyComparison.sick.previous} last month
                  </p>
                  <div className="mt-3 h-4 w-full rounded-full bg-white border border-red-200 overflow-hidden">
                    <div
                      className="h-full rounded-full bg-red-500 transition-all duration-500"
                      style={{ width: `${sickPillFill}%` }}
                    ></div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Bradford Indicator Key */}
          <div className="mt-6 rounded-xl border border-slate-200 bg-slate-50 p-4">
            <h4 className="text-sm font-semibold text-slate-900 mb-2">Bradford Indicator Key</h4>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-2 text-xs">
              <span className="rounded border border-emerald-200 bg-emerald-50 px-3 py-2 text-emerald-700">0-49: Low</span>
              <span className="rounded border border-blue-200 bg-blue-50 px-3 py-2 text-blue-700">50-99: Monitor</span>
              <span className="rounded border border-amber-200 bg-amber-50 px-3 py-2 text-amber-700">100-199: Review</span>
              <span className="rounded border border-orange-200 bg-orange-50 px-3 py-2 text-orange-700">200-399: Formal Stage</span>
              <span className="rounded border border-rose-200 bg-rose-50 px-3 py-2 text-rose-700">400+: Critical</span>
            </div>
            <p className="mt-3 text-xs leading-5 text-slate-600">
              These are internal review indicators, not statutory UK thresholds. Before any action, review the individual circumstances,
              apply the absence policy consistently, and consider pregnancy-related, disability-related, authorised absence and reasonable adjustments.
            </p>
          </div>

          {/* Trend Charts */}
          <div className="mt-8">
            <div className="bg-white/80 backdrop-blur-sm rounded-2xl shadow-xl border border-white/20 overflow-hidden">
              <div className="px-6 py-4 bg-gradient-to-r from-purple-100 to-indigo-100 border-b border-purple-200">
                <h3 className="text-xl font-semibold text-gray-900">Lateness & Sickness Trend</h3>
                <p className="text-sm text-gray-600 mt-1">
                  Monthly totals within the holiday policy window
                </p>
              </div>
              <div className="p-6">
                {trendData.length === 0 ? (
                  <p className="text-sm text-gray-500">No trend data available yet.</p>
                ) : (
                  <div className="grid grid-cols-1 xl:grid-cols-2 gap-6">
                    <div className="rounded-xl border border-orange-100 bg-orange-50/60 p-4">
                      <h4 className="text-sm font-semibold text-orange-700 mb-3">Lateness per Month</h4>
                      <div className="h-72 w-full">
                        <VictoryChart theme={VictoryTheme.material} domainPadding={{ x: 16, y: 10 }}>
                          <VictoryAxis
                            tickValues={lateBarData.map((point) => point.x)}
                            tickFormat={(tick) => trendData[tick - 1]?.shortMonth || ""}
                            style={{ tickLabels: { angle: -30, fontSize: 9, padding: 18 } }}
                          />
                          <VictoryAxis
                            dependentAxis
                            tickFormat={(tick) => `${tick}`}
                            style={{ tickLabels: { fontSize: 10 } }}
                          />
                          <VictoryBar
                            data={lateBarData}
                            barWidth={16}
                            style={{ data: { fill: "#f59e0b" } }}
                          />
                        </VictoryChart>
                      </div>
                    </div>

                    <div className="rounded-xl border border-red-100 bg-red-50/60 p-4">
                      <h4 className="text-sm font-semibold text-red-700 mb-3">Sickness per Month</h4>
                      <div className="h-72 w-full">
                        <VictoryChart theme={VictoryTheme.material} domainPadding={{ x: 16, y: 10 }}>
                          <VictoryAxis
                            tickValues={sickBarData.map((point) => point.x)}
                            tickFormat={(tick) => trendData[tick - 1]?.shortMonth || ""}
                            style={{ tickLabels: { angle: -30, fontSize: 9, padding: 18 } }}
                          />
                          <VictoryAxis
                            dependentAxis
                            tickFormat={(tick) => `${tick}`}
                            style={{ tickLabels: { fontSize: 10 } }}
                          />
                          <VictoryBar
                            data={sickBarData}
                            barWidth={16}
                            style={{ data: { fill: "#ef4444" } }}
                          />
                        </VictoryChart>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Overtime Section */}
          <div className="mt-8">
            <div className="bg-white/80 backdrop-blur-sm rounded-2xl shadow-xl border border-white/20 overflow-hidden">
              <div className="px-6 py-4 bg-gradient-to-r from-emerald-100 to-teal-100 border-b border-emerald-200">
                <h3 className="text-xl font-semibold text-gray-900">Overtime Bids</h3>
                <p className="text-sm text-gray-600 mt-1">
                  Accepted overtime hours, offer acceptance rate, and unaccepted bids
                </p>
              </div>
              <div className="p-6 space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div className="rounded-xl border border-green-100 bg-green-50 p-4">
                    <p className="text-xs font-semibold text-green-700 uppercase">Accepted Offers</p>
                    <p className="mt-1 text-2xl font-bold text-green-800">{overtimeAcceptance.accepted || 0}</p>
                  </div>
                  <div className="rounded-xl border border-amber-100 bg-amber-50 p-4">
                    <p className="text-xs font-semibold text-amber-700 uppercase">Unaccepted Offers</p>
                    <p className="mt-1 text-2xl font-bold text-amber-800">{overtimeAcceptance.unaccepted || 0}</p>
                  </div>
                  <div className="rounded-xl border border-blue-100 bg-blue-50 p-4">
                    <p className="text-xs font-semibold text-blue-700 uppercase">Acceptance Rate</p>
                    <p className="mt-1 text-2xl font-bold text-blue-800">{overtimeAcceptance.percentage || 0}%</p>
                  </div>
                </div>

                <div className="rounded-xl border border-teal-100 bg-teal-50/60 p-4">
                  <h4 className="text-sm font-semibold text-teal-700 mb-3">Extra Hours Worked per Month</h4>
                  {overtimeMonthlyHours.length === 0 ? (
                    <p className="text-sm text-gray-500">No accepted overtime bids found.</p>
                  ) : (
                    <div className="h-72 w-full">
                      <VictoryChart theme={VictoryTheme.material} domainPadding={{ x: 16, y: 10 }}>
                        <VictoryAxis
                          tickValues={overtimeBarData.map((point) => point.x)}
                          tickFormat={(tick) => formatMonthKey(overtimeMonthlyHours[tick - 1]?.monthKey)}
                          style={{ tickLabels: { angle: -30, fontSize: 9, padding: 18 } }}
                        />
                        <VictoryAxis
                          dependentAxis
                          tickFormat={(tick) => `${tick}`}
                          style={{ tickLabels: { fontSize: 10 } }}
                        />
                        <VictoryBar
                          data={overtimeBarData}
                          barWidth={16}
                          style={{ data: { fill: "#0d9488" } }}
                        />
                      </VictoryChart>
                    </div>
                  )}
                </div>

                <div>
                  <h4 className="text-sm font-semibold text-gray-900 mb-3">Unaccepted Bids (Accepted = null or 0)</h4>
                  {unacceptedBids.length === 0 ? (
                    <p className="text-sm text-gray-500">No unaccepted bids found.</p>
                  ) : (
                    <div className="overflow-x-auto rounded-xl border border-gray-100">
                      <table className="min-w-full divide-y divide-gray-200 text-sm">
                        <thead className="bg-gray-50/80">
                          <tr>
                            <th className="px-3 py-3 text-left font-semibold text-gray-700">Bid ID</th>
                            <th className="px-3 py-3 text-left font-semibold text-gray-700">Overtime ID</th>
                            <th className="px-3 py-3 text-left font-semibold text-gray-700">Hours</th>
                            <th className="px-3 py-3 text-left font-semibold text-gray-700">Created</th>
                            <th className="px-3 py-3 text-left font-semibold text-gray-700">Accepted</th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-100 bg-white">
                          {unacceptedBids.map((bid) => (
                            <tr key={bid.id} className="hover:bg-gray-50">
                              <td className="px-3 py-3 whitespace-nowrap text-gray-900">{bid.id}</td>
                              <td className="px-3 py-3 whitespace-nowrap text-gray-900">{bid.OvertimeId}</td>
                              <td className="px-3 py-3 whitespace-nowrap text-gray-900">{bid.Hours}</td>
                              <td className="px-3 py-3 whitespace-nowrap text-gray-900">{formatDateSafe(bid.CreatedAt, "dd/MM/yyyy HH:mm")}</td>
                              <td className="px-3 py-3 whitespace-nowrap text-gray-900">{bid.Accepted ?? 0}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* Sick Leave History Section */}
          <div className="mt-8">
            <div className="bg-white/80 backdrop-blur-sm rounded-2xl shadow-xl border border-white/20 overflow-hidden">
              <div className="px-6 py-4 bg-gradient-to-r from-indigo-100 to-emerald-100 border-b border-indigo-200">
                <h3 className="text-xl font-semibold text-gray-900 flex items-center gap-2">
                  🩺 Sickness History & Trends
                </h3>
                <p className="text-sm text-gray-600 mt-1">
                  Back to Work records and AI-generated insights for this team member
                </p>
              </div>
              <div className="p-6 space-y-6">
                <div className="rounded-2xl border border-indigo-100 bg-indigo-50/60 p-5">
                  <div className="flex flex-col gap-3 md:flex-row md:items-start md:justify-between">
                    <div className="flex-1">
                      <p className="text-sm font-semibold text-indigo-700">
                        AI Sickness Trend Insight
                      </p>
                      {sickTrends ? (
                        <p className="mt-2 whitespace-pre-line text-sm text-gray-800">
                          {sickTrends}
                        </p>
                      ) : (
                        <p className="mt-2 text-sm text-gray-500">
                          Trend analysis has not been generated for this employee yet.
                        </p>
                      )}
                    </div>
                    <div className="text-sm text-gray-600 md:text-right">
                      <p className="font-medium uppercase tracking-wide text-gray-500">
                        Last generated
                      </p>
                      <p className="mt-1 text-gray-900">
                        {sickTrendsGeneratedAt ? formatDateSafe(sickTrendsGeneratedAt, "dd/MM/yyyy HH:mm") : "Not available"}
                      </p>
                    </div>
                  </div>
                </div>
                <div>
                  <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between mb-3">
                    <h4 className="text-lg font-semibold text-gray-900">
                      Back to Work Records
                    </h4>
                    <span className="text-sm text-gray-500">
                      {sickRecords.length} record{sickRecords.length === 1 ? '' : 's'}
                    </span>
                  </div>
                  {sickRecords.length === 0 ? (
                    <p className="text-sm text-gray-500">
                      No back to work forms have been submitted for this employee yet.
                    </p>
                  ) : (
                    <div className="overflow-x-auto rounded-xl border border-gray-100">
                      <table className="min-w-full divide-y divide-gray-200 text-sm">
                        <thead className="bg-gray-50/80">
                          <tr>
                            <th className="px-3 py-3 text-left font-semibold text-gray-700">Id</th>
                            <th className="px-3 py-3 text-left font-semibold text-gray-700">Company</th>
                            <th className="px-3 py-3 text-left font-semibold text-gray-700">Staff Id</th>
                            <th className="px-4 py-3 text-left font-semibold text-gray-700">Staff Name</th>
                            <th className="px-6 py-3 text-left font-semibold text-gray-700">Conversation (Q → A)</th>
                            <th className="px-3 py-3 text-left font-semibold text-gray-700">Start Date</th>
                            <th className="px-3 py-3 text-left font-semibold text-gray-700">Return Date</th>
                            <th className="px-3 py-3 text-left font-semibold text-gray-700">Reason</th>
                            <th className="px-3 py-3 text-left font-semibold text-gray-700">Fit For Work</th>
                            <th className="px-3 py-3 text-left font-semibold text-gray-700">Adjustments</th>
                            <th className="px-3 py-3 text-left font-semibold text-gray-700">Self Cert</th>
                            <th className="px-3 py-3 text-left font-semibold text-gray-700">Doctor Letter</th>
                            <th className="px-3 py-3 text-left font-semibold text-gray-700">Manager</th>
                            <th className="px-3 py-3 text-left font-semibold text-gray-700">Meeting Held</th>
                            <th className="px-3 py-3 text-left font-semibold text-gray-700">Manager Notes</th>
                            <th className="px-3 py-3 text-left font-semibold text-gray-700">Fit Confirmed</th>
                            <th className="px-3 py-3 text-left font-semibold text-gray-700">Further Support</th>
                            <th className="px-3 py-3 text-left font-semibold text-gray-700">Checklist Completed</th>
                            <th className="px-3 py-3 text-left font-semibold text-gray-700">Created At</th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-100 bg-white">
                          {sickRecords.map((record) => (
                            <tr key={record.Id} className="hover:bg-indigo-50/50">
                              <td className="px-3 py-3 whitespace-nowrap text-gray-900">{record.Id}</td>
                              <td className="px-3 py-3 whitespace-nowrap text-gray-900">{record.CompanyCode}</td>
                              <td className="px-3 py-3 whitespace-nowrap text-gray-900">{record.StaffId}</td>
                              <td className="px-4 py-3 whitespace-nowrap text-gray-900">{record.StaffName}</td>
                              <td className="px-6 py-3 text-gray-900 max-w-xl break-words">
                                {(() => {
                                  const raw = record.AIConversation || record.AIConversations || record.AIConversationJson || record.AIConversationJSON || '[]';
                                  try {
                                    const arr = typeof raw === 'string' ? JSON.parse(raw) : raw;
                                    if (!Array.isArray(arr) || arr.length === 0) return <span className="text-sm text-gray-500">No conversation</span>;
                                    return (
                                      <div className="space-y-2 text-sm">
                                        {arr.map((qa, i) => (
                                          <div key={i} className="pb-1">
                                            <div className="font-semibold text-gray-800">{qa.Question}</div>
                                            <div className="text-gray-700">{qa.Answer}</div>
                                          </div>
                                        ))}
                                      </div>
                                    );
                                  } catch (e) {
                                    return <span className="text-sm text-gray-500">Invalid conversation</span>;
                                  }
                                })()}
                              </td>
                              <td className="px-3 py-3 whitespace-nowrap text-gray-900">{formatDateSafe(record.StartDate)}</td>
                              <td className="px-3 py-3 whitespace-nowrap text-gray-900">{formatDateSafe(record.ReturnDate)}</td>
                              <td className="px-3 py-3 text-gray-900">{record.Reason}</td>
                              <td className="px-3 py-3 whitespace-nowrap text-gray-900">{record.FitForWork === 1 ? 'Yes' : 'No'}</td>
                              <td className="px-3 py-3 text-gray-900">{record.Adjustments}</td>
                              <td className="px-3 py-3 whitespace-nowrap text-gray-900">{record.SelfCert === 1 ? 'Yes' : 'No'}</td>
                              <td className="px-3 py-3 whitespace-nowrap text-gray-900">{record.DRLetter === 1 ? 'Yes' : 'No'}</td>
                              <td className="px-3 py-3 whitespace-nowrap text-gray-900">{record.ManagerName}</td>
                              <td className="px-3 py-3 whitespace-nowrap text-gray-900">{record.MeetingHeld === 1 ? 'Yes' : 'No'}</td>
                              <td className="px-3 py-3 text-gray-900">{record.ManagerNotes}</td>
                              <td className="px-3 py-3 whitespace-nowrap text-gray-900">{record.FitToReturnConfirmed === 1 ? 'Yes' : 'No'}</td>
                              <td className="px-3 py-3 whitespace-nowrap text-gray-900">{record.FurtherSupportRequired === 1 ? 'Yes' : 'No'}</td>
                              <td className="px-3 py-3 whitespace-nowrap text-gray-900">{record.ChecklistCompletedAt ? formatDateSafe(record.ChecklistCompletedAt, "dd/MM/yyyy HH:mm") : ''}</td>
                              <td className="px-3 py-3 whitespace-nowrap text-gray-900">{record.CreatedAt ? formatDateSafe(record.CreatedAt, "dd/MM/yyyy HH:mm") : ''}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* Late Reasons Section */}
          {lateReasons.length > 0 && (
            <div className="mt-8">
              <div className="bg-white/80 backdrop-blur-sm rounded-2xl shadow-xl border border-white/20 overflow-hidden">
                <div className="px-6 py-4 bg-gradient-to-r from-orange-100 to-red-100 border-b border-orange-200">
                  <h3 className="text-xl font-semibold text-gray-900 flex items-center gap-2">
                    ⏰ Late Arrival History
                  </h3>
                  <p className="text-sm text-gray-600 mt-1">
                    Detailed records of late arrivals and reasons provided
                  </p>
                </div>

                {/* Desktop Table View */}
                <div className="hidden lg:block overflow-x-auto">
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50/80">
                      <tr>
                        <th className="px-6 py-4 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">
                          Date
                        </th>
                        <th className="px-6 py-4 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">
                          Clock In
                        </th>
                        <th className="px-6 py-4 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">
                          Duration Late
                        </th>
                        <th className="px-6 py-4 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">
                          Reason
                        </th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-100">
                      {lateReasons.map((record, index) => (
                        <tr key={index} className="hover:bg-orange-50 transition-colors">
                          <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                            {record.ShiftDate}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700">
                            {record.ClockinTime}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm">
                            <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-orange-100 text-orange-800">
                              {record.MinutesLate}
                            </span>
                          </td>
                          <td className="px-6 py-4 text-sm text-gray-700 max-w-xs">
                            {record.LateReason}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                {/* Mobile Card View */}
                <div className="lg:hidden">
                  <div className="p-4 space-y-4">
                    {lateReasons.map((record, index) => (
                      <div
                        key={index}
                        className="bg-white rounded-xl border border-gray-200 p-4 shadow-sm hover:shadow-md transition-all duration-200"
                      >
                        <div className="flex justify-between items-start mb-3">
                          <div>
                            <h4 className="font-semibold text-gray-900 text-base">
                              {record.ShiftDate}
                            </h4>
                            <p className="text-sm text-gray-600">
                              Clock in: {record.ClockinTime}
                            </p>
                          </div>
                          <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-orange-100 text-orange-800">
                            {record.MinutesLate} late
                          </span>
                        </div>
                        <div className="bg-gray-50 rounded-lg p-3">
                          <p className="text-sm font-medium text-gray-700 mb-1">Reason:</p>
                          <p className="text-sm text-gray-600">{record.LateReason}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Additional Components */}
      <div className="px-4 py-6 sm:px-6 lg:px-8 bg-gradient-to-b from-transparent to-indigo-50/50">
        <div className="max-w-7xl mx-auto space-y-8">
          <div className="bg-white/80 backdrop-blur-sm rounded-2xl shadow-xl border border-white/20 overflow-hidden">
            <div className="px-6 py-4 bg-gradient-to-r from-indigo-100 to-purple-100 border-b border-indigo-200">
              <h3 className="text-xl font-semibold text-gray-900 flex items-center gap-2">
                📈 Performance Analytics
              </h3>
              <p className="text-sm text-gray-600 mt-1">
                Monthly tasks and training progress overview
              </p>
            </div>
            <div className="p-6">
              <MonthlyTasksChart staffId={staff.id} />
            </div>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-1 gap-6">
            <div className="bg-white/80 backdrop-blur-sm rounded-2xl shadow-xl border border-white/20 overflow-hidden">
              <div className="px-6 py-4 bg-gradient-to-r from-green-100 to-emerald-100 border-b border-green-200">
                <h3 className="text-xl font-semibold text-gray-900 flex items-center gap-2">
                  🎓 Training Statistics
                </h3>
                <p className="text-sm text-gray-600 mt-1">
                  Personal training progress and achievements
                </p>
              </div>
              <div className="p-6">
                <TrainingStats />
              </div>
            </div>

            <div className="bg-white/80 backdrop-blur-sm rounded-2xl shadow-xl border border-white/20 overflow-hidden">
              <div className="px-6 py-4 bg-gradient-to-r from-blue-100 to-cyan-100 border-b border-blue-200">
                <h3 className="text-xl font-semibold text-gray-900 flex items-center gap-2">
                  📚 Task Training
                </h3>
                <p className="text-sm text-gray-600 mt-1">
                  Individual task completion and training records
                </p>
              </div>
              <div className="p-6">
                <TaskTrainingIndv id={staff.id} />
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}